import { Injectable } from "@angular/core";
import { Observable, Subscription, take } from "rxjs";
import { NzModalRef, NzModalService, NzModalState } from "ng-zorro-antd/modal";
import { AddUserModalComponent } from "../components/add_user_modal/add_user_modal.component";
import { AddUserCompletion, AddUserContext, AddUserModalData } from "../components/add_user_modal/add_user_modal.types";
import { UserAssignmentCompletion, UserAssignmentModalComponent, UserAssignmentModalData } from "../components/add_user_modal/user_assignment_modal.component";
import { AccountSettingsService } from "./account_settings.service";
import { LoginService } from "./login.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import { accessId, assignmentRoles, UserAccessError } from "./assessment_user_access.util";
import { AssessmentAssignmentRole, UserAccessContext, UserAccessQuery } from "./assessment_user_access.types";
import { UserDialogOrigin, UserDialogOriginGuard } from "./user_dialog_origin.util";

interface DialogSession { identity: string; token: string; zIndex: number; isCurrent: () => boolean; }
interface OpenedDialog<R> { ref: NzModalRef<unknown, R>; fallback?: () => R | undefined; }

@Injectable({ providedIn: "root" })
export class AddUserLaunchService {
    private active = false;
    constructor(private readonly accounts: AccountSettingsService, private readonly login: LoginService,
        private readonly modals: NzModalService) {}

    openUserSettings(): Observable<AddUserCompletion> { return this.open({ kind: "user_settings" }); }
    openAssessment(productLicenseId: number, defaultRole?: AssessmentAssignmentRole, origin?: UserDialogOrigin): Observable<AddUserCompletion> {
        if (defaultRole !== undefined) assignmentRoles([defaultRole]);
        return this.open({ kind: "assessment", productLicenseId: accessId(productLicenseId), defaultRole }, origin);
    }

    openAssessmentEdit(productLicenseId: number, targetUserId: number, origin?: UserDialogOrigin): Observable<UserAssignmentCompletion> {
        const license = accessId(productLicenseId), target = accessId(targetUserId);
        return this.launch({ productLicenseId: license, targetUserId: target }, origin, undefined, (access, session) => {
            if (access.assessment?.productLicenseId !== license || access.target?.userId !== target) {
                throw new UserAccessError("This user or assessment is unavailable.", "ACCESS_DENIED");
            }
            const data: UserAssignmentModalData = { access, productLicenseId: license, targetUserId: target,
                sessionIdentity: session.identity, token: session.token, isCurrentOrigin: session.isCurrent };
            const ref: NzModalRef<UserAssignmentModalComponent, UserAssignmentCompletion> = this.modals.create<UserAssignmentModalComponent, UserAssignmentModalData, UserAssignmentCompletion>({
                nzTitle: "Edit User — assessment access", nzContent: UserAssignmentModalComponent, nzData: data,
                nzFooter: null, nzWidth: 620, nzMaskClosable: false, nzKeyboard: true, nzCloseOnNavigation: true, nzZIndex: session.zIndex,
                nzOnCancel: () => { ref.getContentComponent()?.close(); return false; }
            });
            return { ref };
        });
    }

    private open(context: AddUserContext, origin?: UserDialogOrigin): Observable<AddUserCompletion> {
        return this.launch(context.kind === "assessment" ? { productLicenseId: context.productLicenseId } : {}, origin,
            context.kind === "assessment" ? context.defaultRole : undefined, (access, session) => {
                const unresolved = this.accounts.getUnresolvedInvitation();
                const licenseId = context.kind === "assessment" ? context.productLicenseId : null;
                if (unresolved && (unresolved.productLicenseId ?? null) !== licenseId) {
                    throw new UserAccessError("A previous invitation is unresolved. Reopen Add User from its original assessment or User Settings to resolve it before creating another user.", "INVITATION_RESOLUTION_REQUIRED");
                }
                const allowed = context.kind === "user_settings" ? access.capabilities.canInviteWithoutAssessment
                    : access.capabilities.canInviteOnAssessment && access.assessment?.productLicenseId === context.productLicenseId;
                if (!allowed) throw new UserAccessError(context.kind === "user_settings"
                    ? "Invite users from an assessment you currently edit. Only authorized administrators can invite without an assessment."
                    : "You can no longer invite users to this assessment.", "ACCESS_DENIED");
                const data: AddUserModalData = { context, access, sessionIdentity: session.identity, token: session.token, isCurrentOrigin: session.isCurrent,
                    requiresEvidenceUpload: origin?.requiresEvidenceUpload === true,
                    hideAssessmentRoles: origin?.hideAssessmentRoles === true && context.kind === "assessment" && !!context.defaultRole,
                    ...(unresolved ? { unresolvedInvitation: unresolved } : {}),
                    isGlobalAdmin: this.login.getCurrentUserRole()?.trim().toLowerCase() === "global admin" &&
                        access.capabilities.invitationOrganizationRoles.some((role) => role !== "User") };
                const ref: NzModalRef<AddUserModalComponent, AddUserCompletion> = this.modals.create<AddUserModalComponent, AddUserModalData, AddUserCompletion>({
                    nzTitle: "Add User", nzContent: AddUserModalComponent, nzData: data, nzFooter: null, nzWidth: context.kind === "assessment" ? "min(960px, calc(100vw - 24px))" : 560,
                    nzClassName: context.kind === "assessment" ? "cb4-assignment-modal" : "",
                    nzStyle: context.kind === "assessment" ? { top: "5vh" } : {},
                    nzMaskClosable: false, nzKeyboard: true, nzCloseOnNavigation: true, nzZIndex: session.zIndex,
                    nzOnCancel: () => { ref.getContentComponent()?.close(); return false; }
                });
                const content = ref.getContentComponent();
                return { ref, fallback: () => content?.persisted ?? undefined };
            });
    }

    private launch<R extends { outcome: string }>(query: UserAccessQuery, origin: UserDialogOrigin | undefined, defaultRole: string | undefined,
        create: (access: UserAccessContext, session: DialogSession) => OpenedDialog<R>): Observable<R | { outcome: "canceled" }> {
        return new Observable((observer) => {
            if (this.active) { observer.error(new UserAccessError("A user dialog is already open.", "DIALOG_ALREADY_OPEN")); return; }
            const subscriptions = new Subscription();
            let opened: OpenedDialog<R> | undefined;
            let settled = false;
            let focusAtSettlement: boolean | undefined;
            const parents = [...this.modals.openModals];
            const identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot()), token = this.login.getToken();
            const guard = new UserDialogOriginGuard(origin, parents, query.productLicenseId ?? null, defaultRole);
            this.active = true;
            const sessionCurrent = () => !!identity && !!token && identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot()) && token === this.login.getToken();
            const current = () => sessionCurrent() && guard.isCurrent();
            const finish = (result: R | { outcome: "canceled" }) => {
                if (settled || observer.closed) return;
                settled = true;
                focusAtSettlement = current();
                observer.next(result); observer.complete();
            };
            const cancel = () => finish({ outcome: "canceled" });
            const release = () => { guard.release(sessionCurrent() && (focusAtSettlement ?? guard.isCurrent())); this.active = false; };
            // Register cleanup before subscribing: capability/origin streams can emit synchronously.
            observer.add(() => {
                subscriptions.unsubscribe();
                if (opened && opened.ref.getState() !== NzModalState.CLOSED) {
                    opened.ref.afterClose.pipe(take(1)).subscribe(release);
                    opened.ref.destroy();
                } else release();
            });
            try {
                if (!current()) { cancel(); return; }
                guard.activate(parents, cancel);
                if (observer.closed) return;
                if (!current()) { cancel(); return; }
                subscriptions.add(this.login.getUserUpdates().subscribe(() => { if (!current()) cancel(); }));
                if (observer.closed) return;
                subscriptions.add(this.accounts.getUserAccess(query).subscribe({
                    next: (access) => {
                        if (!current()) { cancel(); return; }
                        try {
                            opened = create(access, { identity: identity!, token: token!, zIndex: guard.zIndex,
                                isCurrent: () => !observer.closed && !settled && current() });
                            subscriptions.add(opened.ref.afterClose.pipe(take(1)).subscribe((result) => {
                                finish(current() ? result ?? opened?.fallback?.() ?? { outcome: "canceled" } : { outcome: "canceled" });
                            }));
                        } catch (error) { observer.error(error); }
                    }, error: (error: unknown) => { if (current()) observer.error(error); else cancel(); }
                }));
            } catch (error) { observer.error(error); }
        });
    }
}
